/* eslint-disable import/no-unresolved */

'use client';

import { useEffect, useRef, useState } from 'react';
import { Navigation, Keyboard, A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/a11y';
import './featured.scss';
import DOMPurify from 'dompurify';
import { NextIcon, PrevIcon } from 'components/atoms/Icons/iconList';
import PropTypes from 'prop-types';
import VideoCard from 'components/molecules/videoCard';
import { focusSlideIntoView } from 'helpers/ui';

export default function FeaturedSection({ data }) {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const swiperRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const featuredData = (data?.elements || [])
    .slice()
    .filter((e) => e && e.enabled)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper) return undefined;

    const handleSlideChange = () => {
      const realIndex = typeof swiper.realIndex === 'number' ? swiper.realIndex : 0;
      setActiveIndex(realIndex);
      setAtStart(!!swiper.isBeginning);
      setAtEnd(!!swiper.isEnd);
    };

    swiper.on('slideChange', handleSlideChange);
    handleSlideChange();

    return () => {
      swiper.off('slideChange', handleSlideChange);
    };
  }, []);

  return (
    <div className='featured' onFocus={(e) => focusSlideIntoView(swiperRef.current, e.target)}>
      <div className='titleContainer'>
        <div className='titleContainer_title proxima-nova-bold' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.title) }} />
        <div className='titleContainer_subtitle' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.subtitle) }} />
      </div>

      <Swiper
        className='featuredCarouselContainer'
        modules={[Navigation, Keyboard, A11y]}
        loop={false}
        slidesPerView='auto'
        spaceBetween={20}
        keyboard={{ enabled: true, onlyInViewport: true }}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          setTimeout(() => {
            try {
              if (prevRef.current && nextRef.current && swiper.navigation) {
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
                swiper.navigation.destroy();
                swiper.navigation.init();
                swiper.navigation.update();
              }
            } catch (err) {
              console.warn('failed to re-init navigation', err);
            }
          }, 0);
        }}
      >
        {featuredData.map((element, idx) => (
          <SwiperSlide key={element.order ?? element.title ?? idx}>
            <VideoCard
              element={element}
              isActive={idx === activeIndex}
              className='featuredCard'
              body={(
                <div className='featuredDataContainer'>
                  <div
                    className='featuredDataContainer_title proxima-nova-bold'
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.title) }}
                  />
                  <div className='featuredDataContainer_body' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.body) }} />
                </div>
               )}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      <div className='flex gap-[16px] mx-[40px] mt-8'>
        <button
          type='button'
          ref={prevRef}
          className={`featuredControl prev ${atStart ? 'disabled' : ''}`}
          aria-label='Previous'
        >
          <PrevIcon color='#39A9F7' />
        </button>
        <button
          type='button'
          ref={nextRef}
          className={`featuredControl next ${atEnd ? 'disabled' : ''}`}
          aria-label='Next'
        >
          <NextIcon color='#39A9F7' />
        </button>
      </div>
    </div>
  );
}

FeaturedSection.propTypes = {
  data: PropTypes.shape({
    enabled: PropTypes.bool.isRequired,
    order: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    elements: PropTypes.arrayOf(
      PropTypes.shape({
        videoUrl: PropTypes.string.isRequired,
        thumbnailUrl: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        body: PropTypes.string,
        order: PropTypes.number.isRequired,
        enabled: PropTypes.bool.isRequired,
      }),
    ).isRequired,
  }),
};
